"use client";

import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface TierLabelProps {
  label: string;
  color: { label: string; background: string };
  fontSizeMultiplier?: number; // Default: 2.0
  onLabelChange?: (newLabel: string) => void;
  onFontSizeChange?: (multiplier: number) => void;
  isPresentationMode?: boolean;
}

export function TierLabel({
  label,
  color,
  fontSizeMultiplier = 2.0,
  onLabelChange,
  onFontSizeChange,
  isPresentationMode = false,
}: TierLabelProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [editMultiplier, setEditMultiplier] = useState(2.0);
  const [error, setError] = useState<string>("");
  const inputRef = useRef<HTMLInputElement>(null);
  const savedValuesRef = useRef<{ label: string; multiplier: number } | null>(null);

  const MAX_LENGTH = 50;

  // If we have saved values and props haven't updated yet, use saved values
  // Once props match saved values, clear the ref
  let displayLabel = label;
  let displayMultiplier = fontSizeMultiplier;

  if (savedValuesRef.current) {
    if (savedValuesRef.current.label === label && savedValuesRef.current.multiplier === fontSizeMultiplier) {
      // Props have updated, clear the saved values
      savedValuesRef.current = null;
    } else {
      // Props haven't updated yet, use saved values
      displayLabel = savedValuesRef.current.label;
      displayMultiplier = savedValuesRef.current.multiplier;
    }
  }

  const handleClick = () => {
    if (onLabelChange && !isPresentationMode) {
      setEditValue(displayLabel);
      setEditMultiplier(displayMultiplier);
      setError("");
      setIsDialogOpen(true);
    }
  };

  const handleSave = () => {
    const trimmed = editValue.trim();

    if (!trimmed) {
      setError("Label cannot be empty");
      return;
    }

    if (trimmed.length > MAX_LENGTH) {
      setError(`Label must be ${MAX_LENGTH} characters or less`);
      return;
    }

    // Store the values we're about to save so we can show them immediately
    savedValuesRef.current = {
      label: trimmed,
      multiplier: editMultiplier,
    };

    // Close dialog
    setIsDialogOpen(false);
    setError("");

    // Save changes (will eventually update props and clear savedValuesRef)
    if (trimmed !== label && onLabelChange) {
      onLabelChange(trimmed);
    }

    if (editMultiplier !== fontSizeMultiplier && onFontSizeChange) {
      onFontSizeChange(editMultiplier);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsDialogOpen(open);
    if (!open) {
      setError("");
    }
  };

  return (
    <>
      <div
        className={`rounded-md flex !items-center !justify-center font-bold transition-opacity text-center ${
          !isPresentationMode && onLabelChange
            ? "cursor-pointer hover:opacity-90"
            : ""
        }`}
        style={{
          backgroundColor: color.background,
          color: color.label,
          fontSize: `calc(var(--tiers-root-size) * ${isDialogOpen ? editMultiplier : displayMultiplier})`,
          lineHeight: 1.2,
          wordBreak: "break-word",
          hyphens: "auto",
          paddingTop: 'calc(var(--tiers-root-size) * 0.5)',
          paddingBottom: 'calc(var(--tiers-root-size) * 0.5)',
          margin: 'calc(var(--tiers-root-size) * 0.5)',
        }}
        onClick={!isPresentationMode ? handleClick : undefined}
        title={
          !isPresentationMode && onLabelChange
            ? "Double-click to edit"
            : undefined
        }
      >
        {isDialogOpen ? editValue || displayLabel : displayLabel}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Tier Label</DialogTitle>
            <DialogDescription>
              Customize the label text and font size. Changes are previewed live
              in the background.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Label Text</label>
              <Input
                ref={inputRef}
                type="text"
                value={editValue}
                onChange={(e) => {
                  setEditValue(e.target.value);
                  setError("");
                }}
                onKeyDown={handleKeyDown}
                maxLength={MAX_LENGTH}
                placeholder="Enter tier label"
                className={error ? "border-red-500" : ""}
                autoFocus
              />
              {error && <p className="text-sm text-red-500">{error}</p>}
              <p className="text-xs text-muted-foreground">
                {editValue.length}/{MAX_LENGTH} characters
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="font-size-slider" className="text-sm font-medium">
                  Font Size
                </label>
                <span className="text-sm text-muted-foreground" aria-live="polite">
                  {editMultiplier.toFixed(1)}x
                </span>
              </div>
              <input
                type="range"
                min="0.2"
                max="6.0"
                step="0.1"
                value={editMultiplier}
                onChange={(e) => setEditMultiplier(parseFloat(e.target.value))}
                className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer"
                id="font-size-slider"
                aria-label="Font size multiplier"
                aria-valuemin={0.2}
                aria-valuemax={6.0}
                aria-valuenow={editMultiplier}
                aria-valuetext={`${editMultiplier.toFixed(1)} times`}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0.2x</span>
                <span>6.0x</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSave}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
